'use client';

import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { APP_VERSION_RELOAD_DELAY_MS } from '@/consts/app-version';
import { fetchAppVersionFile } from '@/utils/fetchAppVersionFile';

const STALE_CHUNK_SESSION_KEY = 'sf-app-stale-chunk-reloaded';
const STALE_CHUNK_LAST_RELOAD_KEY = 'sf-app-stale-chunk-last-reload';
const STALE_CHUNK_RELOAD_COOLDOWN_MS = 60 * 1000;

const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk [\d]+ failed/i,
  /Loading CSS chunk/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /\b\w\[\w\] is not a function\b/, // webpack runtime indirection
  /Invalid or unexpected token/i, // HTML returned where JS/CSS expected
];

function looksLikeStaleChunkError(message: string | undefined, filename: string | undefined): boolean {
  if (filename && (filename.includes('/_next/static/') || filename.endsWith('.css'))) {
    return true;
  }

  if (!message) {
    return false;
  }

  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

async function reloadIfNewBuildAvailable(): Promise<boolean> {
  const versionFile = await fetchAppVersionFile();

  if (!versionFile?.buildId) {
    return false;
  }

  return true;
}

function isWithinReloadCooldown(): boolean {
  try {
    const lastReloadRaw = localStorage.getItem(STALE_CHUNK_LAST_RELOAD_KEY);

    if (!lastReloadRaw) {
      return false;
    }

    const lastReload = Number(lastReloadRaw);

    if (!Number.isFinite(lastReload)) {
      return false;
    }

    return Date.now() - lastReload < STALE_CHUNK_RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markReloadTimestamp(): void {
  try {
    localStorage.setItem(STALE_CHUNK_LAST_RELOAD_KEY, String(Date.now()));
  } catch {
    // ignore: cooldown is best-effort
  }
}

function reloadOnce(): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (sessionStorage.getItem(STALE_CHUNK_SESSION_KEY)) {
    return;
  }

  if (isWithinReloadCooldown()) {
    sessionStorage.setItem(STALE_CHUNK_SESSION_KEY, '1');
    return;
  }

  sessionStorage.setItem(STALE_CHUNK_SESSION_KEY, '1');
  markReloadTimestamp();

  toast.info('Updating to the latest version…', {
    autoClose: APP_VERSION_RELOAD_DELAY_MS,
    closeOnClick: false,
    draggable: false,
  });

  window.setTimeout(() => {
    window.location.reload();
  }, APP_VERSION_RELOAD_DELAY_MS);
}

export function useStaleChunkRecovery() {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleError = (event: Event) => {
      const target = event.target as HTMLElement | null;

      if (target && 'tagName' in target) {
        const tag = target.tagName?.toLowerCase();

        if (tag === 'link' || tag === 'script') {
          const href =
            tag === 'link'
              ? (target as HTMLLinkElement).href
              : (target as HTMLScriptElement).src;

          if (looksLikeStaleChunkError(undefined, href)) {
            void reloadIfNewBuildAvailable().then((shouldReload) => {
              if (shouldReload) {
                reloadOnce();
              }
            });
            return;
          }
        }
      }

      const errorEvent = event as ErrorEvent;

      if (!looksLikeStaleChunkError(errorEvent.message, errorEvent.filename)) {
        return;
      }

      void reloadIfNewBuildAvailable().then((shouldReload) => {
        if (shouldReload) {
          reloadOnce();
        }
      });
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as { message?: string; name?: string; filename?: string } | undefined;
      const message = reason?.message ?? reason?.name;

      if (!looksLikeStaleChunkError(message, reason?.filename)) {
        return;
      }

      void reloadIfNewBuildAvailable().then((shouldReload) => {
        if (shouldReload) {
          reloadOnce();
        }
      });
    };

    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);
}

export function isStaleChunkSentryEvent(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false;
  }

  if (CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(value))) {
    return true;
  }

  return value.includes('/_next/static/');
}
